import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';

// The only screen an on-leave staff member sees. CJ 2026-08-04, D229.
//
// They are off the schedule and out of headcount, but they keep a login so
// they can say when they are back and what they can work — instead of that
// arriving by text and living in a manager's head. Submitting alerts their
// café's managers; a manager brings them back in one action.
//
// Everything else in the app is unreachable for them: getActingUser() still
// resolves active-only server-side, so this is not a UI-only restriction.
//
// Sub-components at module scope — one defined inside a render body is a new
// type every render, which remounts the subtree and drops focus mid-typing.

const C = {
  ink: '#1c1f1d', inkSoft: '#4a4d4a', faint: '#7a7d79',
  paper: '#f6f4ee', card: '#fff', line: '#d8d2c2',
  forest: '#2f5d50', gold: '#b48a47', crimson: '#8a3a3a',
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function prettyDate(ymd) {
  if (!ymd) return '';
  const d = new Date(String(ymd).slice(0, 10) + 'T12:00:00Z');
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
}

function Submitted({ submission, onEdit }) {
  return (
    <div style={{ background: '#e8f0ea', border: `1px solid ${C.forest}`,
                  borderRadius: 10, padding: '16px 18px' }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: C.forest }}>
        Thanks — your manager has been told
      </div>
      <div style={{ fontSize: 14, color: C.ink, marginTop: 8 }}>
        You said you are back from <strong>{prettyDate(submission.return_date)}</strong>.
      </div>
      {submission.availability_note && (
        <div style={{ fontSize: 13, color: C.inkSoft, marginTop: 6 }}>
          Availability: “{submission.availability_note}”
        </div>
      )}
      <div style={{ fontSize: 12, color: C.faint, marginTop: 10 }}>
        You will get full access back once a manager confirms it. Until then you
        are off the schedule.
      </div>
      <button onClick={onEdit}
        style={{ marginTop: 12, background: 'none', border: 'none', padding: 0,
                  color: C.forest, textDecoration: 'underline', cursor: 'pointer',
                  fontSize: 13, fontFamily: 'inherit' }}>
        Change what I sent
      </button>
    </div>
  );
}

export function OnLeaveReturnView({ user, onSignOut }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ return_date: '', availability_note: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const headers = { 'x-user-id': String(user.id) };

  async function load() {
    setLoading(true); setErr('');
    try {
      const r = await api('GET', '/api/leave/me', null, headers);
      setData(r);
      if (r.submission) {
        setForm({
          return_date: r.submission.return_date || '',
          availability_note: r.submission.availability_note || '',
        });
      }
    } catch (e) {
      setErr(e?.message || 'Could not load your details');
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function submit() {
    if (!form.return_date) { setErr('Please pick the date you are back.'); return; }
    setBusy(true); setErr('');
    try {
      await api('POST', '/api/leave/return', {
        return_date: form.return_date,
        availability_note: form.availability_note || null,
      }, headers);
      setEditing(false);
      await load();
    } catch (e) {
      let msg = e?.message || 'Could not send that';
      try { const p = JSON.parse(msg); msg = p.error || msg; } catch { /* plain */ }
      setErr(msg);
    } finally { setBusy(false); }
  }

  const showForm = editing || !data?.submission;

  return (
    <div style={{ minHeight: '100vh', background: C.paper, color: C.ink,
                  fontFamily: 'ui-sans-serif, system-ui, -apple-system, sans-serif',
                  padding: '2rem 1rem' }}>
      <div style={{ maxWidth: 460, margin: '0 auto', display: 'flex',
                    flexDirection: 'column', gap: 18 }}>

        <div>
          <div style={{ fontFamily: 'ui-serif, Georgia, serif', fontSize: 26,
                        fontWeight: 600, color: C.forest, lineHeight: 1.15 }}>
            See you when you’re back
          </div>
          <div style={{ fontSize: 14, color: C.inkSoft, marginTop: 6 }}>
            Hi {String(user.name || '').split(' ')[0]} — your account is on leave, so
            you’re off the schedule for now.
            {data?.last_day && <> Your last day was <strong>{prettyDate(data.last_day)}</strong>.</>}
          </div>
        </div>

        {loading && <div style={{ fontSize: 14, color: C.faint }}>Loading…</div>}

        {!loading && !showForm && data?.submission && (
          <Submitted submission={data.submission} onEdit={() => setEditing(true)} />
        )}

        {!loading && showForm && (
          <div style={{ background: C.card, border: `1px solid ${C.line}`,
                        borderRadius: 10, padding: '16px 18px' }}>
            <div style={{ fontSize: 15, fontWeight: 700 }}>When are you back?</div>
            <div style={{ fontSize: 13, color: C.inkSoft, margin: '4px 0 14px' }}>
              Tell us the date and when you can work. Your manager gets this straight
              away, and they’ll switch your account back on.
            </div>

            <label style={{ display: 'block', fontSize: 12, fontWeight: 700,
                            color: C.inkSoft, marginBottom: 4 }}>
              First day back
            </label>
            <input type="date" value={form.return_date}
              onChange={(e) => setForm((f) => ({ ...f, return_date: e.target.value }))}
              style={{ width: '100%', padding: '9px 10px', fontSize: 15,
                        border: `1px solid ${C.line}`, borderRadius: 7,
                        fontFamily: 'inherit', marginBottom: 14 }} />

            <label style={{ display: 'block', fontSize: 12, fontWeight: 700,
                            color: C.inkSoft, marginBottom: 4 }}>
              What can you work? <span style={{ fontWeight: 400 }}>(optional)</span>
            </label>
            <textarea value={form.availability_note} rows={4}
              placeholder="e.g. weekends only until mid-January, then any weekday morning"
              onChange={(e) => setForm((f) => ({ ...f, availability_note: e.target.value }))}
              style={{ width: '100%', padding: '9px 10px', fontSize: 14,
                        border: `1px solid ${C.line}`, borderRadius: 7,
                        fontFamily: 'inherit', resize: 'vertical' }} />
            <div style={{ fontSize: 11, color: C.faint, marginTop: 4 }}>
              Your manager will set your proper availability when you’re back — this
              just tells them what to expect.
            </div>

            {err && (
              <div style={{ fontSize: 13, color: C.crimson, marginTop: 10 }}>{err}</div>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button onClick={submit} disabled={busy || !form.return_date}
                style={{ background: C.forest, color: '#fff', border: 'none',
                          borderRadius: 7, padding: '9px 18px', fontSize: 14,
                          fontWeight: 600, fontFamily: 'inherit',
                          cursor: (busy || !form.return_date) ? 'not-allowed' : 'pointer',
                          opacity: (busy || !form.return_date) ? 0.5 : 1 }}>
                {busy ? 'Sending…' : data?.submission ? 'Update' : 'Send to my manager'}
              </button>
              {data?.submission && (
                <button onClick={() => { setEditing(false); setErr(''); }}
                  style={{ background: 'none', border: `1px solid ${C.line}`,
                            borderRadius: 7, padding: '9px 14px', fontSize: 14,
                            fontFamily: 'inherit', cursor: 'pointer', color: C.inkSoft }}>
                  Cancel
                </button>
              )}
            </div>
          </div>
        )}

        {err && !showForm && (
          <div style={{ fontSize: 13, color: C.crimson }}>{err}</div>
        )}

        <div style={{ borderTop: `1px solid ${C.line}`, paddingTop: 12,
                      fontSize: 12, color: C.faint }}>
          Something else to sort out? Talk to your manager directly — this is the
          only screen available while you’re on leave.
          {onSignOut && (
            <>
              {' '}
              <button onClick={onSignOut}
                style={{ background: 'none', border: 'none', padding: 0, color: C.forest,
                          textDecoration: 'underline', cursor: 'pointer', fontSize: 12,
                          fontFamily: 'inherit' }}>
                Sign out
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
